/**
 * Console diagnostics for the "open / apply an AI design" flow.
 *
 * Why this exists
 * ---------------
 * When a user opens a design from the AI Designs tab/rail, the card metrics
 * (production + module count) come from cached maps in the viewer store
 * (`layoutProductionByKey` / `layoutModulesByKey` / `layoutMetricsByKey`), while
 * the 3D layout that actually gets loaded is resolved separately from the captured
 * `modulesLayout` snapshot (or, as a fallback, by re-simulating roof sets). When the
 * two sources disagree, the viewer shows a different design than the card promised.
 *
 * These helpers make the flow observable in the browser console and provide a single
 * place to detect (and let callers auto-correct) a card-vs-viewer mismatch.
 */

import type { SavedRoofModulesLayout } from '../../../../../@types/types';
import { useViewerStore } from '../../../../../store/store';
import { getCategoryMainLayoutKey } from './categoryBestVariantScoring';

const LOG_PREFIX = '[AI-DESIGN-FLOW===]';

/** Module count actually represented by an exact placement snapshot. */
export function countModulesInLayout(layout: SavedRoofModulesLayout | null | undefined): number {
    if (!Array.isArray(layout)) return 0;
    return layout.reduce((sum, roof) => sum + (Array.isArray(roof?.moduleArr) ? roof.moduleArr.length : 0), 0);
}

/** Live viewer metrics after a layout has been applied + shade recalculated. */
export function readLiveViewerMetrics(): {
    modules: number;
    production: number;
    multiplier: number | null;
} {
    const state = useViewerStore.getState() as any;
    return {
        modules: typeof state.modeCount === 'number' && Number.isFinite(state.modeCount) ? state.modeCount : 0,
        production:
            typeof state.actualWattage === 'number' && Number.isFinite(state.actualWattage)
                ? state.actualWattage
                : 0,
        multiplier:
            typeof state.moduleMultiplierAverage === 'number' && Number.isFinite(state.moduleMultiplierAverage)
                ? state.moduleMultiplierAverage
                : null,
    };
}

/** Expected card metrics for a design, read from the same maps the AI cards render from. */
export function readExpectedCardMetrics(
    layoutKey: string,
    categoryLabel?: string,
): {
    key: string;
    modules: number | null;
    production: number | null;
    multiplier: number | null;
    efficiency: number | null;
    solarAccess: number | null;
} {
    const state = useViewerStore.getState() as any;
    const prod = (state.layoutProductionByKey ?? {}) as Record<string, number>;
    const mods = (state.layoutModulesByKey ?? {}) as Record<string, number>;
    const metrics = (state.layoutMetricsByKey ?? {}) as Record<
        string,
        { multiplier?: number | null; efficiency?: number | null; solarAccess?: number | null }
    >;

    // Category cards are keyed by the category-main key, not the raw layout key.
    const catKey = categoryLabel ? getCategoryMainLayoutKey(categoryLabel) : undefined;
    const key =
        catKey && (prod[catKey] != null || mods[catKey] != null)
            ? catKey
            : prod[layoutKey] != null || mods[layoutKey] != null
              ? layoutKey
              : (catKey ?? layoutKey);

    const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
    const metricRow = metrics[key];
    return {
        key,
        modules: num(mods[key]),
        production: num(prod[key]),
        multiplier: num(metricRow?.multiplier),
        efficiency: num(metricRow?.efficiency),
        solarAccess: num(metricRow?.solarAccess),
    };
}

/**
 * Whether an exact-placement snapshot can be trusted for a design: it must reproduce the
 * module count shown on the card. A snapshot that disagrees with the card is a corrupt
 * capture (placement vs. metrics taken from different viewer states) and must not be applied,
 * otherwise the viewer loads a different design than the card promised.
 *
 * Returns `true` when there is no card module count to compare against (nothing to contradict).
 */
export function isSnapshotConsistentWithCard(
    layoutKey: string,
    categoryLabel: string | undefined,
    modulesLayout: SavedRoofModulesLayout | null | undefined,
): boolean {
    const snapshotModules = countModulesInLayout(modulesLayout);
    if (snapshotModules <= 0) return false;
    const card = readExpectedCardMetrics(layoutKey, categoryLabel);
    if (card.modules == null || card.modules <= 0) return true;
    return snapshotModules === card.modules;
}

export type AiDesignOpenContext = {
    /** Where the open was triggered from (catalog card, slide rail, wishlist row, …). */
    source: string;
    layoutKey: string;
    categoryLabel?: string;
    selectionKey?: string;
};

/** Open a console group describing the design the user is opening + what the card promised. */
export function logAiDesignOpenStart(ctx: AiDesignOpenContext): void {
    try {
        const expected = readExpectedCardMetrics(ctx.layoutKey, ctx.categoryLabel);
        // eslint-disable-next-line no-console
        console.groupCollapsed(
            `${LOG_PREFIX} open "${ctx.categoryLabel ?? ctx.layoutKey}" (source: ${ctx.source})`,
        );
        // eslint-disable-next-line no-console
        console.log('request', {
            source: ctx.source,
            layoutKey: ctx.layoutKey,
            categoryLabel: ctx.categoryLabel ?? null,
            selectionKey: ctx.selectionKey ?? null,
        });
        // eslint-disable-next-line no-console
        console.log('expected (from card maps)', {
            metricsKey: expected.key,
            modules: expected.modules,
            productionW: expected.production,
            multiplier: expected.multiplier,
        });
    } catch {
        // never let logging break the flow
    }
}

/**
 * Dump exactly what layout data is available for a category at resolution time.
 * This is the key diagnostic for "wrong design opened": it shows whether the winner
 * record / variants / cached layouts actually carry a `modulesLayout` + roof IDs,
 * or whether everything is empty (which forces the default max-fill fallback).
 */
export function logAiDesignResolveInputs(categoryLabel: string | undefined): void {
    if (!categoryLabel) return;
    try {
        const state = useViewerStore.getState() as any;
        const main = (state.categoryMainLayoutByCategory ?? {})[categoryLabel] as
            | {
                  variantId?: string;
                  roofIds?: string[];
                  modulesLayout?: SavedRoofModulesLayout;
                  dir?: unknown;
                  minEff?: unknown;
                  modules?: number;
                  production?: number;
              }
            | undefined;
        const variants = ((state.aiCategoryLayoutVariants ?? {})[categoryLabel] ?? []) as Array<{
            id?: string;
            modulesLayout?: SavedRoofModulesLayout;
            roofIds?: string[];
        }>;
        const matchedVariant = main?.variantId
            ? variants.find((v) => v?.id === main.variantId)
            : undefined;
        const catKey = getCategoryMainLayoutKey(categoryLabel);
        const cachedByKey = (state.layoutModulesLayoutByKey ?? {}) as Record<
            string,
            SavedRoofModulesLayout | undefined
        >;

        // eslint-disable-next-line no-console
        console.log('resolution inputs', {
            dataSource: state.aiDesignsDataSource ?? null,
            winner: main
                ? {
                      variantId: main.variantId ?? null,
                      roofIdCount: main.roofIds?.length ?? 0,
                      modulesInWinnerSnapshot: countModulesInLayout(main.modulesLayout),
                      dir: main.dir ?? null,
                      minEff: main.minEff ?? null,
                      winnerModules: main.modules ?? null,
                      winnerProductionW: main.production ?? null,
                  }
                : 'NO WINNER RECORD for this category',
            variantsAvailable: variants.length,
            matchedVariant: matchedVariant
                ? {
                      modulesInVariantSnapshot: countModulesInLayout(matchedVariant.modulesLayout),
                      roofIdCount: matchedVariant.roofIds?.length ?? 0,
                  }
                : 'no variant matched winner.variantId',
            cachedLayoutForCatKey: countModulesInLayout(cachedByKey[catKey]),
            cachedLayoutKeysAvailable: Object.keys(cachedByKey).length,
        });
    } catch {
        // ignore
    }
}

/** Log how the layout-to-apply was resolved (snapshot vs roof set vs fallback). */
export function logAiDesignResolve(detail: {
    branch: 'modules-layout-snapshot' | 'roof-set-fill' | 'simulate-roof-set' | 'maxfill-fallback' | 'default-maxfill';
    /** Exact placement applied (if any) — module count is computed from it, not its roof-face length. */
    modulesLayout?: SavedRoofModulesLayout | null;
    roofFaceIds?: string[];
    dir?: 'port' | 'land' | 'both' | null;
}): void {
    try {
        // eslint-disable-next-line no-console
        console.log('resolved layout', {
            branch: detail.branch,
            modulesInSnapshot: countModulesInLayout(detail.modulesLayout),
            roofFaceIds: detail.roofFaceIds ?? [],
            roofCount: detail.roofFaceIds?.length ?? 0,
            dir: detail.dir ?? null,
        });
    } catch {
        // ignore
    }
}

/**
 * Compare the live viewer to the **card** metrics (the numbers the user sees and trusts)
 * and log the result. Also reports whether the exact-placement snapshot we applied agrees
 * with the card — if it doesn't, the capture itself is corrupt (auto-correct can't fix that).
 *
 * Returns:
 *  - `mismatch`: live viewer module count differs from the card.
 *  - `snapshotMatchesCard`: the applied snapshot reproduces the card count (safe to re-apply).
 */
export function verifyAppliedDesign(args: {
    layoutKey: string;
    categoryLabel?: string;
    /** Exact placement that was applied, if any. */
    expectedModulesLayout?: SavedRoofModulesLayout | null;
}): {
    mismatch: boolean;
    snapshotMatchesCard: boolean;
    cardModules: number | null;
    snapshotModules: number;
    liveModules: number;
} {
    let mismatch = false;
    let snapshotMatchesCard = false;
    let cardModules: number | null = null;
    let snapshotModules = 0;
    let liveModules = 0;
    try {
        const card = readExpectedCardMetrics(args.layoutKey, args.categoryLabel);
        const live = readLiveViewerMetrics();
        liveModules = live.modules;
        cardModules = card.modules;
        snapshotModules = countModulesInLayout(args.expectedModulesLayout);

        // The card is the source of truth (it's what the user picked).
        mismatch = cardModules != null && cardModules > 0 && cardModules !== live.modules;
        snapshotMatchesCard =
            cardModules != null && cardModules > 0 && snapshotModules > 0 && snapshotModules === cardModules;
        const productionMismatch =
            !mismatch &&
            card.production != null &&
            card.production > 0 &&
            Math.round(card.production) !== Math.round(live.production);

        const payload = {
            cardModules,
            liveModules: live.modules,
            modulesInAppliedSnapshot: snapshotModules,
            snapshotMatchesCard,
            cardProductionW: card.production,
            liveProductionW: live.production,
            cardMultiplier: card.multiplier,
            liveMultiplier: live.multiplier,
            productionMismatch,
        };
        if (mismatch) {
            // eslint-disable-next-line no-console
            console.warn(`${LOG_PREFIX} MISMATCH — viewer does not match the opened design card`, payload);
            if (snapshotModules > 0 && !snapshotMatchesCard) {
                // eslint-disable-next-line no-console
                console.warn(
                    `${LOG_PREFIX} the applied placement snapshot also disagrees with the card — ` +
                        `the captured layout for this design is corrupt (snapshot has ${snapshotModules}, card says ${cardModules}).`,
                );
            }
        } else if (productionMismatch) {
            // eslint-disable-next-line no-console
            console.warn(
                `${LOG_PREFIX} production drift — modules match but live shade calc differs from card`,
                payload,
            );
        } else {
            // eslint-disable-next-line no-console
            console.log('verified — viewer matches card', payload);
        }
    } catch {
        // ignore
    }
    return { mismatch, snapshotMatchesCard, cardModules, snapshotModules, liveModules };
}

export {
    resolveActiveAiDesignApplyKeys,
    syncActiveAiDesignProductionFromCard,
    syncLiveViewerMetricsFromCard,
} from '../../../../../helpers/aiDesignProductionSync';

/**
 * Audit every category winner right after they are selected, so card metrics and the layout
 * that will be applied are guaranteed consistent. Flags any category where:
 *   - the `__category_main__` map count (card) ≠ the winner's `modules`, or
 *   - the captured `modulesLayout` count ≠ the winner's `modules`.
 * Use this to verify the "Max Production with Best Roofs" class of bug doesn't exist elsewhere.
 */
export function logCategoryWinnersAudit(): void {
    try {
        const state = useViewerStore.getState() as any;
        const winners = (state.categoryMainLayoutByCategory ?? {}) as Record<
            string,
            {
                modules?: number;
                production?: number;
                roofIds?: string[];
                modulesLayout?: SavedRoofModulesLayout;
                variantId?: string;
                dir?: unknown;
                minEff?: unknown;
            }
        >;
        const prod = (state.layoutProductionByKey ?? {}) as Record<string, number>;
        const mods = (state.layoutModulesByKey ?? {}) as Record<string, number>;

        const rows: Record<string, unknown> = {};
        let anyInconsistent = false;
        for (const [category, w] of Object.entries(winners)) {
            const catKey = getCategoryMainLayoutKey(category);
            const cardModules = typeof mods[catKey] === 'number' ? mods[catKey] : null;
            const winnerModules = typeof w.modules === 'number' ? w.modules : null;
            const snapshotModules = countModulesInLayout(w.modulesLayout);
            const cardVsWinnerOk = cardModules == null || winnerModules == null || cardModules === winnerModules;
            const snapshotOk = snapshotModules === 0 || winnerModules == null || snapshotModules === winnerModules;
            const consistent = cardVsWinnerOk && snapshotOk;
            if (!consistent) anyInconsistent = true;
            rows[category] = {
                cardModules,
                winnerModules,
                snapshotModules,
                roofCount: w.roofIds?.length ?? 0,
                cardProductionW: typeof prod[catKey] === 'number' ? Math.round(prod[catKey]) : null,
                winnerProductionW: typeof w.production === 'number' ? Math.round(w.production) : null,
                source: w.variantId ? 'variant' : 'layoutKey',
                consistent,
            };
        }

        // eslint-disable-next-line no-console
        console.groupCollapsed(
            `${LOG_PREFIX} category winners audit ${anyInconsistent ? '— ⚠ INCONSISTENCIES FOUND' : '— all consistent'}`,
        );
        // eslint-disable-next-line no-console
        console.table(rows);
        // eslint-disable-next-line no-console
        console.groupEnd();
    } catch {
        // ignore
    }
}

/** Log the roof set Gemini returned for each category (the roof combinations driving the layouts). */
export function logGeminiRoofCombinations(
    roofCombinations: Array<{ name?: string; roofFaceIds?: string[] }> | null | undefined,
): void {
    try {
        let combos = roofCombinations ?? [];
        if (!combos.length) {
            const fromStore = (useViewerStore.getState() as any).aiResolvedRoofCombinations;
            if (Array.isArray(fromStore)) combos = fromStore;
        }
        const rows: Record<string, unknown> = {};
        for (const combo of combos) {
            if (!combo?.name) continue;
            rows[combo.name] = {
                roofCount: combo.roofFaceIds?.length ?? 0,
                roofFaceIds: (combo.roofFaceIds ?? []).join(', '),
            };
        }
        // eslint-disable-next-line no-console
        console.groupCollapsed(`${LOG_PREFIX} Gemini roof combinations per category (${Object.keys(rows).length})`);
        // eslint-disable-next-line no-console
        console.table(rows);
        // eslint-disable-next-line no-console
        console.groupEnd();
    } catch {
        // ignore
    }
}

/** Close the console group opened by `logAiDesignOpenStart`. */
export function logAiDesignOpenEnd(note?: string): void {
    try {
        if (note) {
            // eslint-disable-next-line no-console
            console.log(note);
        }
        // eslint-disable-next-line no-console
        console.groupEnd();
    } catch {
        // ignore
    }
}
